import React, { useRef, useEffect } from "react";
import { Container, Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Landing.css";
import { Helmet } from "react-helmet";

import KinesiologoIntro from "./components/KinesiologoIntro";
import HeaderProfesionales from "./components/HeaderProfesionales";
import ContactFormComponent from "./components/ContactForm";
import FooterProfesion from "./components/FooterProfesion";
import Planes from "./components/Planes";
import AnimatedFeatureBoxes from "./components/Cajas";
import CarruselProfesionales from "./components/CarruselProfesionales";
import CloudBackground from "./components/CloudBackground";
import PreguntasFrecuentes from "./components/PreguntasFrecuentes";
import { limpiarBaulSiExpirado } from "../../services/referralBaul";

export default function DemoKine() {
  const pageTitle = `Clinifica`;
  const pageDescription = `Clinifica: Revoluciona tus servicios de kinesiología con agendamiento fácil, pagos automáticos y inteligencia artificial. Profesionaliza y digitaliza tu consulta hoy.`;

  const pricingSectionRef = useRef(null);
  const [searchParams] = useSearchParams();

  const scrollToPricing = () => {
    pricingSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Si el referido expiró (28 días), limpiar explícitamente ANTES de renderizar el Header o los Planes
  limpiarBaulSiExpirado();

  // NUEVO: Tracking de visita única a la landing
  useEffect(() => {
    if (window.fbq) {
      window.fbq('trackCustom', 'viewlanding');
    }
  }, []);

  // NUEVO: Auto-scroll al detectar un hash en la URL al montar Landing
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace("#", "");
      const timer = setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 600);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const section = searchParams.get("section");
    
    if (section === "planes") {
      const timer = setTimeout(() => {
        scrollToPricing();
      }, 600);

      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content="https://www.Clinifica.com/pro" />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://www.Clinifica.com/pro" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "Article",
            headline: pageTitle,
            description: pageDescription,
            url: "https://www.Clinifica.com/pro",
          })}
        </script>
      </Helmet>

      {/* Header Flotante */}
      <HeaderProfesionales scrollToPricing={scrollToPricing} />

      {/* Sección Hero */}
      <Box 
        sx={{ 
          position: 'relative',
          pt: { xs: '92px', md: '110px' },
          // ELIMINADO overflow: hidden para que no corte las sombras
        }} 
        className="hero-section"
      >
        {/* Mancha ovalada azul decorativa de fondo (ESTÁTICA - NO TOCADA) */}
        {/* Valores originales que te gustaron: top 95% mobile, 90% desktop */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '95%', md: '90%' }, 
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '140%', md: '140%' },
            height: { xs: '20%', md: '35%' },
            maxWidth: { xs: '800px', md: '1900px' },
            maxHeight: { xs: '400px', md: '700px' },
            background: { 
              xs: 'radial-gradient(ellipse, rgba(70, 105, 232, 1) 10%, rgba(10, 86, 162, 0.95) 15%, rgba(70, 105, 232, 0.85) 30%, rgba(10, 86, 162, 0.7) 45%, rgba(70, 105, 232, 0.5) 100%, transparent 80%)',
              md: 'radial-gradient(ellipse, rgba(70, 105, 232, 0.9) 10%, rgba(10, 86, 162, 0.85) 15%, rgba(70, 105, 232, 0.7) 30%, rgba(10, 86, 162, 0.5) 45%, rgba(70, 105, 232, 0.3) 100%, transparent 80%)'
            },
            borderRadius: '50%',
            filter: { xs: 'blur(80px)', md: 'blur(120px)' },
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />

        {/* Círculo animado con movimiento en infinito */}
        <Box
          sx={{
            position: 'absolute',
            // ALINEADO con la mancha estática para que compartan el mismo centro
            top: { xs: '95%', md: '90%' }, 
            left: '50%',
            width: { xs: '160px', md: '380px' },
            height: { xs: '160px', md: '380px' },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(10, 86, 162, 0.5) 0%, rgba(10, 86, 162, 0.3) 40%, rgba(10, 86, 162, 0.1) 70%, transparent 85%)',
            filter: { xs: 'blur(30px)', md: 'blur(45px)' },
            zIndex: 0,
            pointerEvents: 'none',
            animation: { 
              xs: 'infinityMoveMobile 12s linear infinite', 
              md: 'infinityMoveDesktop 10s linear infinite'
            },
          }}
        />

        {/* Keyframes Ajustados */}
        <style>
          {`
            @keyframes infinityMoveMobile {
              0%   { transform: translate(-50%, -50%) translate(0, 0); }
              12.5% { transform: translate(-50%, -50%) translate(70px, -35px); }
              25%  { transform: translate(-50%, -50%) translate(140px, 0); }
              37.5% { transform: translate(-50%, -50%) translate(70px, 35px); }
              50%  { transform: translate(-50%, -50%) translate(0, 0); }
              62.5% { transform: translate(-50%, -50%) translate(-70px, -35px); }
              75%  { transform: translate(-50%, -50%) translate(-140px, 0); }
              87.5% { transform: translate(-50%, -50%) translate(-70px, 35px); }
              100% { transform: translate(-50%, -50%) translate(0, 0); }
            }

            @keyframes infinityMoveDesktop {
              0%   { transform: translate(-50%, -50%) translate(0, 0); }
              12.5% { transform: translate(-50%, -50%) translate(200px, -70px); }
              25%  { transform: translate(-50%, -50%) translate(400px, 0); }
              37.5% { transform: translate(-50%, -50%) translate(200px, 70px); }
              50%  { transform: translate(-50%, -50%) translate(0, 0); }
              62.5% { transform: translate(-50%, -50%) translate(-200px, -70px); }
              75%  { transform: translate(-50%, -50%) translate(-400px, 0); }
              87.5% { transform: translate(-50%, -50%) translate(-200px, 70px); }
              100% { transform: translate(-50%, -50%) translate(0, 0); }
            }
          `}
        </style>
        
        {/* Contenido principal */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <KinesiologoIntro scrollToPricing={scrollToPricing} />
        </Container>
      </Box>

      <Container maxWidth="lg">
        <div id="caracteristicas">
          <AnimatedFeatureBoxes />
        </div>
      </Container>

      <Box sx={{ position: 'relative' }} className="planes-section" ref={pricingSectionRef} id="planes-section">
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Planes />
        </Container>
      </Box>

      <CarruselProfesionales />

      <PreguntasFrecuentes />

      <Container maxWidth="lg">
        <div id="contacto" style={{ marginBottom: '100px' }}>
          <ContactFormComponent />
        </div>
      </Container>

      <FooterProfesion />
    </>
  );
}
